import React, { useState } from "react";
import { Animate } from "react-move";

import { Vote } from "@/service/Model";
import { Svg } from "@/styles";
import Markdown from "@/utils/markdown";

import { OptionEnum, OptionModeEnum, OptionTypeEnum } from "../index";
import {
  Bubble,
  Description,
  Image,
  ImageOverlay,
  ImageWrapperExtended,
  Meta,
  Name,
} from "./styles";

export type ImageTileExtendedProps = {
  mode: OptionModeEnum;
  type: OptionTypeEnum;
  answer?: OptionEnum;
  percent?: number;
  checkmark?: boolean;
  name?: string;
  description?: string;
  icons: IIcons;
  interactionId: string;
  slideId: string;
  optionId: string;
  imageUrl: string;
  overlayContent?: string;
  isClickable: boolean;
  onClick?: (vote: Vote, isVoted: boolean) => void;
};

export const ImageOptionExtended: React.FC<ImageTileExtendedProps> = (
  props: ImageTileExtendedProps
): React.JSX.Element => {
  const {
    type,
    mode,
    answer,
    name,
    description,
    optionId,
    overlayContent,
    isClickable,
    imageUrl,
    percent,
    checkmark,
    icons,
    interactionId,
    slideId,
    onClick,
  } = props;

  const [selected, setSelected] = useState<boolean>(false);

  const handleClick = (): void => {
    if (!isClickable || !onClick) {
      return;
    }
    onClick({ interactionId, slideId, optionId }, !!answer);
    setSelected(!selected);
  };

  return (
    <ImageWrapperExtended
      $mode={mode}
      $type={type}
      $answer={answer}
      $bottomRadius={true}
      $isClickable={isClickable}
      onClick={handleClick}
    >
      <Image $answer={answer} $imageUrl={imageUrl} $isClickable={isClickable}>
        {overlayContent && (
          <Animate
            start={{ opacity: 0 }}
            enter={[
              { opacity: [1], timing: { duration: 750 } },
              {
                opacity: [0],
                timing: { delay: 1500, duration: 750 },
              },
            ]}
            update={[
              { opacity: [1], timing: { duration: 750 } },
              {
                opacity: [0],
                timing: { delay: 1500, duration: 750 },
              },
            ]}
            children={(data: any) => (
              <ImageOverlay
                key={optionId}
                $opacity={data.opacity}
                dangerouslySetInnerHTML={{
                  __html: Markdown(overlayContent),
                }}
              />
            )}
          />
        )}
      </Image>
      {typeof percent === "number" && (
        <Bubble data-qa="bubble:percent">{percent}%</Bubble>
      )}
      {checkmark && (
        <Bubble data-qa="bubble:checkmark">
          <Svg contents={icons.BubbleCheckmark} height={24} width={24} />
        </Bubble>
      )}
      {name && (
        <Meta $invert={!!answer}>
          <Name>{name}</Name>
          {description && (
            <Description
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              dangerouslySetInnerHTML={{
                __html: Markdown(description),
              }}
            />
          )}
        </Meta>
      )}
    </ImageWrapperExtended>
  );
};
